import { useEffect, useRef } from 'react';

import { clampZoom, fitViewportToContainer } from '../core/viewport';
import useStageSize from './useStageSize';

const ZOOM_STEP = 1.15;

const useViewport = (editor) => {
  const { containerRef, size } = useStageSize();
  const fittedKeyRef = useRef(null);
  const touchedRef = useRef(false);
  const docKey = editor.hasDocument ? `${editor.width}x${editor.height}` : null;

  useEffect(() => {
    if (!docKey) {
      fittedKeyRef.current = null;
      touchedRef.current = false;
      return;
    }
    if (!size.width || !size.height) {
      return;
    }
    if (fittedKeyRef.current !== docKey) {
      touchedRef.current = false;
    }
    if (touchedRef.current) {
      return;
    }
    fittedKeyRef.current = docKey;
    editor.fitToScreen(size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey, size.width, size.height]);

  const touch = () => {
    touchedRef.current = true;
  };

  const zoomBy = (factor) => {
    touch();
    editor.zoomAt(clampZoom(editor.viewport.zoom * factor), size.width / 2, size.height / 2);
  };

  const fitZoom = editor.hasDocument
    ? fitViewportToContainer(editor.width, editor.height, size.width, size.height).zoom
    : 1;

  return {
    containerRef,
    containerSize: size,
    relativeZoom: editor.viewport.zoom / (fitZoom || 1),
    release: () => {
      touchedRef.current = false;
    },
    panBy: (dx, dy) => {
      touch();
      editor.panViewport(dx, dy);
    },
    setViewport: (partial) => {
      touch();
      editor.setViewport(partial);
    },
    zoomIn: () => zoomBy(ZOOM_STEP),
    zoomOut: () => zoomBy(1 / ZOOM_STEP),
    fit: () => {
      touchedRef.current = false;
      fittedKeyRef.current = docKey;
      editor.fitToScreen(size);
    },
  };
};

export default useViewport;
